import { useRef, useState } from "react";
import { motion, useScroll, useTransform, useMotionValueEvent, useReducedMotion, useSpring } from "framer-motion";
import { Stage, ChapterTag } from "@/components/Reveal";
import { Traveler, Thread } from "@/components/Motifs";
import { useMouseParallax } from "@/hooks/useMouseParallax";
import SanctumWorld from "@/components/SanctumWorld";
import { SanctumFallbackWorld } from "@/components/SanctumFallbackWorld";

const LINES = [
  { id: "attention", text: "“What has your attention lately?”" },
  { id: "future", text: "“The room leans toward what has not happened yet.”" },
  { id: "climb", text: "“The room makes the climb visible.”" },
];

const SANCTUM_RANGES = {
  doorway: [0.34, 0.42, 0.5, 0.56],
  title: [0.57, 0.64, 0.71, 0.77],
};

const SanctumLine = ({ text, i, progress }) => {
  const a = 0.74 + i * 0.04;
  const opacity = useTransform(progress, [a, a + 0.045], [0, 1]);
  const y = useTransform(progress, [a, a + 0.045], [26, 0]);
  return (
    <motion.div className="th-sanctum-line" style={{ opacity, y }} data-testid={`sanctum-line-${i}`}>
      <span>0{i + 1}</span>
      <em>{text}</em>
    </motion.div>
  );
};

const useSanctumMotion = (progress, webglReady, reducedMotion) => {
  const frameOp = useTransform(progress, [0.16, 0.3], [1, 0]);
  const frameScale = useTransform(progress, [0.14, 0.34], reducedMotion ? [1, 1.06] : [1, 2.35]);
  const envOp = useTransform(progress, [0.18, 0.38], [0, 1]);
  const envScale = useTransform(progress, [0.18, 0.44], reducedMotion ? [1, 1] : [1.08, 1]);
  const camZ = useTransform(progress, [0.2, 1], reducedMotion ? [-8, 8] : [-140, 170]);
  const archL = useTransform(progress, [0, 1], reducedMotion ? [8, -8] : [40, -60]);
  const archR = useTransform(progress, [0, 1], reducedMotion ? [10, -6] : [90, -30]);
  const archC = useTransform(progress, [0, 1], reducedMotion ? [8, -8] : [60, -40]);
  const archD = useTransform(progress, [0, 1], reducedMotion ? [10, -6] : [110, -20]);
  const veilF = useTransform(progress, [0, 1], reducedMotion ? [6, -6] : [40, -90]);
  const planeA = useTransform(progress, [0, 1], reducedMotion ? [8, -8] : [70, -70]);
  const planeB = useTransform(progress, [0, 1], reducedMotion ? [10, -6] : [120, -40]);
  const compactViewport = typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches;
  const [mx, my] = useMouseParallax(1, !webglReady && !compactViewport && !reducedMotion);
  return {
    frameOp,
    frameScale,
    envOp,
    envScale,
    camZ,
    archL,
    archR,
    archC,
    archD,
    veilF,
    planeA,
    planeB,
    rotY: useTransform(mx, [-0.5, 0.5], reducedMotion ? [0, 0] : [-0.8, 0.8]),
    rotX: useTransform(my, [-0.5, 0.5], reducedMotion ? [0, 0] : [0.55, -0.55]),
    descOp: useTransform(progress, [0.88, 0.94], [0, 1]),
    descY: useTransform(progress, [0.88, 0.94], reducedMotion ? [6, 0] : [20, 0]),
    threadIn: useTransform(progress, [0.02, 0.3], [0, 1]),
    threadInOp: useTransform(progress, [0.3, 0.4], [1, 0]),
    frameRot: useTransform(progress, [0.14, 0.34], reducedMotion ? [0, 0] : [0, 1.4]),
  };
};

export default function Sanctum() {
  const ref = useRef(null);
  const { scrollYProgress: p } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const smoothP = useSpring(p, { stiffness: 105, damping: 30, mass: 0.38, restDelta: 0.0005 });
  const reducedMotion = Boolean(useReducedMotion());
  const [webglReady, setWebglReady] = useState(false);
  const animation = useSanctumMotion(smoothP, webglReady, reducedMotion);
  const progressRef = useRef(0);
  progressRef.current = smoothP.get();
  useMotionValueEvent(smoothP, "change", (v) => {
    progressRef.current = v;
  });

  return (
    <section id="scene-sanctum" ref={ref} className="th-scene" style={{ height: "360vh" }} data-testid="scene-sanctum">
      <div className="th-sticky">
        <ChapterTag index="07" name="Sanctum" />
        <Thread
          d="M 500 -20 C 498 80, 502 160, 500 240 C 498 400, 502 560, 500 700 C 499 810, 468 870, 425 930"
          viewBox="0 0 1000 1000"
          className="absolute inset-0 w-full h-full"
          progress={animation.threadIn}
          opacity={animation.threadInOp}
          stroke="var(--amber)"
          sw={1.3}
        />
        <motion.div className={`absolute inset-0 ${webglReady ? "has-webgl" : ""}`} style={{ opacity: animation.envOp, scale: animation.envScale }}>
          <div className="th-skywash" />
          <SanctumWorld progressRef={progressRef} onReady={setWebglReady} reducedMotion={reducedMotion} />
          {!webglReady && (
            <SanctumFallbackWorld
              camZ={animation.camZ}
              rotX={animation.rotX}
              rotY={animation.rotY}
              archC={animation.archC}
              archL={animation.archL}
              archR={animation.archR}
              archD={animation.archD}
              planeA={animation.planeA}
              planeB={animation.planeB}
              veilF={animation.veilF}
            />
          )}
          <div className="th-sanctum-engraving" aria-hidden="true">SANCTUM</div>
          <div className="th-sanctum-traveler">
            <Traveler className="h-12 md:h-14" data-testid="sanctum-traveler" />
          </div>
        </motion.div>
        <motion.div className="th-iface" style={{ opacity: animation.frameOp, scale: animation.frameScale, rotate: animation.frameRot }} data-testid="sanctum-interface-frame">
          <img src="/assets/img3296.webp" alt="" loading="lazy" decoding="async" />
        </motion.div>
        <Stage progress={smoothP} range={SANCTUM_RANGES.doorway}>
          <h3 className="th-question th-halo">Some conversations open another door.</h3>
        </Stage>
        <Stage progress={smoothP} range={SANCTUM_RANGES.title}>
          <h3 className="th-display th-sanctum-title" data-testid="sanctum-title">
            Sanctum
          </h3>
        </Stage>
        <div className="th-sanctum-copy absolute left-[8%] bottom-[16%] space-y-3 md:space-y-4 z-10">
          {LINES.map((line, i) => (
            <SanctumLine key={line.id} text={line.text} i={i} progress={smoothP} />
          ))}
        </div>
        <motion.p
          className="th-sub absolute bottom-[6%] inset-x-0 text-center px-6"
          style={{ opacity: animation.descOp, y: animation.descY, marginTop: 0 }}
          data-testid="sanctum-desc"
        >
          A deeper layer of Threshold shaped by topic, attention and response.
        </motion.p>
      </div>
    </section>
  );
}
